import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AddNotificationTemplateDialog } from '@/components/admin/notifications/AddNotificationTemplateDialog';
import { useNavigate } from 'react-router-dom';
import { useAdminGetNotificationTemplates } from '@/api/auth/admin/admin';

/**
 * Admin Notification Templates page for managing email notification templates
 */
export default function AdminNotificationTemplatesPage() {
  const navigate = useNavigate();

  const {
    data: templatesData,
    isLoading: isLoadingTemplates,
    error: templatesError,
  } = useAdminGetNotificationTemplates();

  const templates = templatesData?.data?.templates || [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Notification Templates</h1>
          <p className="text-muted-foreground">
            Manage email notification templates and their localized content
          </p>
        </div>
        <AddNotificationTemplateDialog />
      </div>

      {templatesError ? (
        <div className="text-center text-destructive py-8">
          <p>Failed to load templates: {(templatesError as any)?.message || 'Unknown error'}</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Event Name</TableHead>
              <TableHead>Languages</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoadingTemplates ? (
              [...Array(3)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton className="h-4 w-[150px]" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-[80px]" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-[60px]" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-8 w-[80px]" />
                  </TableCell>
                </TableRow>
              ))
            ) : templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No notification templates found. Create your first template to get started.
                </TableCell>
              </TableRow>
            ) : (
              templates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell className="font-medium font-mono">
                    {template.eventName}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {template.localizations && template.localizations.length > 0 ? (
                        template.localizations.map((loc) => (
                          <Badge key={loc.id} variant="secondary">
                            {loc.language}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted-foreground">None</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {template.isActive ? (
                      <Badge variant="default">Active</Badge>
                    ) : (
                      <Badge variant="outline">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/dashboard/admin/notifications/templates/${template.id}`)}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
